import styled from '@emotion/styled';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { Checkbox } from 'antd';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { t, tt } from 'src/lib/player/i18n';
import { getCurrentEvents, reduceEndTime, reduceStartTime } from 'src/lib/player/selectors';
import { buildShareUrl, type ShareParts } from 'src/lib/player/shareUrl';
import { useStore } from 'src/lib/player/store';
import { formatCursorTime } from 'src/lib/player/utils';
import { correctedNow } from 'src/lib/player/utils/clock';
import { IconCopy, IconOk, IconSharePlatformAware } from '../../BasicComponents/Icons';

const COPIED_FEEDBACK_MS = 2 * OneSecondInMillis;

function canUseNativeShare(): boolean {
  return typeof navigator.share === 'function';
}

/**
 * Lets the viewer pick what a link should carry, and hand it on. Rendered inside a popover on
 * desktop and a modal on phones — see ShareButton.
 */
export const ShareDialog: FC = () => {
  const selected = useStore((state) => state.timer.selected);
  const isLive = useStore((state) => state.timer.isLive);
  const viewport = useStore((state) => state.currentViewport);
  const timeMode = useStore((state) => state.options.timeMode);
  const currentEvents = useStore((state) => getCurrentEvents(state));
  const time = useStore((state) => state.timer.time);
  const copyStateTimeoutId = useRef<number | null>(null);

  const startTime = reduceStartTime(currentEvents);
  const endTime = reduceEndTime(currentEvents);
  // No moment worth sharing before the start: the cursor sits at "now", ahead of the event.
  const hasStarted = startTime <= correctedNow();

  const [parts, setParts] = useState<ShareParts>(() => ({
    favorites: selected.length > 0,
    time: !isLive && hasStarted,
    viewport: false,
  }));
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    return () => {
      if (copyStateTimeoutId.current !== null) {
        clearTimeout(copyStateTimeoutId.current);
      }
    };
  }, []);

  const url = useMemo(
    () =>
      buildShareUrl(
        {
          origin: window.location.origin,
          pathname: window.location.pathname,
          selected,
          time,
          viewport,
        },
        parts,
      ),
    [selected, viewport, parts, time],
  );

  function togglePart(part: keyof ShareParts) {
    setParts((current) => ({ ...current, [part]: !current[part] }));
    setCopyState('idle');
  }

  async function handleCopy() {
    const enabled = (Object.keys(parts) as Array<keyof ShareParts>).filter((key) => parts[key]);
    trackEvent('share', { method: 'copy', parts: enabled.join(',') });
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      // Blocked in an iframe without allow="clipboard-write"
      setCopyState('failed');
    }
    if (copyStateTimeoutId.current !== null) {
      clearTimeout(copyStateTimeoutId.current);
    }
    copyStateTimeoutId.current = window.setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);
  }

  async function handleNativeShare() {
    trackEvent('share', { method: 'native' });
    try {
      await navigator.share({ url });
    } catch (err) {
      // Dismissing the sheet rejects with AbortError. That is not a failure, so stay quiet.
      if ((err as Error)?.name !== 'AbortError') console.warn('Native share failed:', err);
    }
  }

  return (
    <Wrapper>
      <Options>
        <Checkbox
          checked={parts.favorites}
          disabled={selected.length === 0}
          onChange={() => togglePart('favorites')}
        >
          {selected.length > 0
            ? `${t('share.favorites')} (${selected.length})`
            : t('share.favoritesEmpty')}
        </Checkbox>
        <Checkbox checked={parts.time} disabled={!hasStarted} onChange={() => togglePart('time')}>
          {hasStarted
            ? tt('share.timeAt', formatCursorTime(time, startTime, endTime, timeMode))
            : t('share.timeNotStarted')}
        </Checkbox>
        <Checkbox
          checked={parts.viewport}
          disabled={viewport == null}
          onChange={() => togglePart('viewport')}
        >
          {t('share.viewport')}
        </Checkbox>
      </Options>

      <UrlField readOnly value={url} onFocus={(e) => e.currentTarget.select()} />

      <Actions>
        <ActionButton type="button" onClick={handleCopy} title={t('share.copyLink')}>
          {copyState === 'copied' ? <IconOk /> : <IconCopy />}
          <span>{copyState === 'copied' ? t('share.copied') : t('share.copyLink')}</span>
        </ActionButton>
        {canUseNativeShare() && (
          <ActionButton type="button" onClick={handleNativeShare} title={t('share.share')}>
            <IconSharePlatformAware />
            <span>{t('share.share')}</span>
          </ActionButton>
        )}
      </Actions>

      {copyState === 'failed' && <Hint>{t('share.copyFailed')}</Hint>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 260px;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UrlField = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--base-color-50);
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  color: var(--text-color);
  background: white;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

/* Real buttons: focus and Enter/Space come for free, and Safari gets an activation source it can
   anchor the native share sheet to. */
const ActionButton = styled.button`
  display: flex;
  flex: 1;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid var(--base-color-50);
  border-radius: 4px;
  background: white;
  color: var(--text-color);
  font: inherit;
  cursor: pointer;
  font-weight: 600;
  user-select: none;

  &:active {
    opacity: 0.6;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: var(--text-color);
  opacity: 0.7;
`;
